import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import { MODEL_STATE_BOOLEAN, ROUTER_PATHS } from '@/shared';
import {
  useFetchTipoComprobantes,
  useUpdateTipoComprobante,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { TABLE_CONSTANTS } from '@/shared/constants/ui';
import { useTableServerSideFiltering } from '@/shared/hooks';
import { useTableFilter } from '@/shared/hooks/useTableFilter';
import { TipoComprobante } from '@/shared/interfaces';
import {
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasPermission,
} from '@/shared/utils';
import { useUiConfirmModalStore } from '@/store/ui';



export type TiposComprobantePageProps = {};

const TiposComprobantePage: React.FC<TiposComprobantePageProps> = () => {
  const navigate = useNavigate();
  const returnUrlTiposComprobantePage =
    ROUTER_PATHS.administration.tipoComprobanteNav;
  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* mutations
  const changeState = useUpdateTipoComprobante({
    enableRedirect: false,
  });

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: TiposComprobantePagingRes,
    isLoading,
    isRefetching,
  } = useFetchTipoComprobantes({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      nombre: searchTerm,
      ...filterObject,
      filterByState: false,
    },
  });

  ///* handlers
  const onEdit = (tipocomprobante: TipoComprobante) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Editar TipoComprobante',
      subtitle: '¿Está seguro que desea editar este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(
          `${returnUrlTiposComprobantePage}/editar/${tipocomprobante.id_tipocomprobante}`
        );
      },
    });
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<TipoComprobante>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'NOMBRE',
        size: 281,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },

      {
        accessorKey: 'codigo_sri',
        header: 'CODIGO SRI',
        size: 250,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo_sri'),
      },

      {
        accessorKey: 'descripcion',
        header: 'DESCRIPCION',
        size: 280,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'descripcion'),
      },

      {
        accessorKey: 'codigo',
        header: 'CODIGO',
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      },

      {
        accessorKey: 'estado',
        header: 'ESTADO',
        size: 159,
        enableSorting: false,
        enableGlobalFilter: false,
        filterVariant: 'select',
        filterSelectOptions: MODEL_STATE_BOOLEAN,
        Cell: ({ row }) => {
          return typeof row.original?.estado === 'boolean' ? (
            <SwitchComponente
              estadoSwitch={row.original?.estado}
              titulo={row.original.estado ? 'SI' : 'NO'}
              setEstadoSwitch={() => {
                if (!hasPermission(PermissionsEnum.api_change_tipocomprobante))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro?',
                  onConfirm: () => {
                    changeState.mutate({
                      id: row.original.id_tipocomprobante!,
                      data: {
                        estado: !row.original.estado,
                      } as any,
                    });
                    setConfirmDialogIsOpen(false);
                  },
                });
              }}
            />
          ) : (
            'N/A'
          );
        },
      },

      {
        accessorKey: 'fecha_creacion',
        header: 'CREADO',
        size: 154,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'MODIFICADO',
        size: 154,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    [changeState, setConfirmDialog, setConfirmDialogIsOpen]
  );

  return (
    <SingleTableBoxScene
      title="Tipos de Comprobante"
      urlRedirect={`${returnUrlTiposComprobantePage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_tipocomprobante)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<TipoComprobante>
        columns={columns}
        data={TiposComprobantePagingRes?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // filters - server side
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={TiposComprobantePagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasPermission(
          PermissionsEnum.api_change_tipocomprobante
        )}
        actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasPermission(PermissionsEnum.api_change_tipocomprobante)}
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default TiposComprobantePage;
